<script setup>
import ReportFilterSelector from './FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  cleanLabelTitle,
  groupLabelsByCustomAttribute,
} from 'dashboard/helper/commons.js';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const activeGroupTab = ref('all');

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];
const contactAttributes =
  useMapGetter(['attributes/getContactAttributes']) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => {
  const colList = [
    columnHelper.accessor('name', {
      header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
      width: 300,
      cell: cellProps => h(SummaryReportLink, cellProps),
    }),
    columnHelper.accessor('conversationsCount', {
      header: t('SUMMARY_REPORTS.CONVERSATIONS'),
      width: 200,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgFirstResponseTime', {
      header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
      width: 200,
      cell: defaulSpanRender,
    }),
  ];

  if (props.type === 'agent') {
    colList.push(
      columnHelper.accessor('avgResolutionTime', {
        header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
        width: 200,
        cell: defaulSpanRender,
      })
    );
  }

  colList.push(
    columnHelper.accessor('avgReplyTime', {
      header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
      width: 200,
      cell: defaulSpanRender,
    })
  );

  if (props.type === 'agent') {
    colList.push(
      columnHelper.accessor('resolutionsCount', {
        header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
        width: 200,
        cell: defaulSpanRender,
      })
    );
  }

  return colList;
});

const renderAvgTime = value => (value ? formatTime(value) : '--');
const renderCount = value => (value ? value.toLocaleString() : '--');

const availableGroups = computed(() => {
  if (props.type !== 'label') return [];
  const grouped = groupLabelsByCustomAttribute(
    rowItems.value || [],
    contactAttributes.value || []
  );
  const tabs = [
    {
      id: 'all',
      name: t('SUMMARY_REPORTS.ALL_LABELS'),
      count: (rowItems.value || []).length,
    },
  ];
  grouped.groups.forEach(group => {
    tabs.push({
      id: String(group.id),
      name: group.name,
      count: group.labels.length,
    });
  });
  if (grouped.otherLabels.length) {
    tabs.push({
      id: 'other',
      name: t('SUMMARY_REPORTS.OTHER_LABELS'),
      count: grouped.otherLabels.length,
    });
  }
  return tabs;
});

const processedRowItems = computed(() => {
  if (props.type !== 'label') {
    return rowItems.value || [];
  }
  const grouped = groupLabelsByCustomAttribute(
    rowItems.value || [],
    contactAttributes.value || []
  );

  if (activeGroupTab.value === 'all') {
    const list = [];
    grouped.groups.forEach(group => {
      group.labels.forEach(label => {
        list.push({
          ...label,
          groupName: group.name,
          groupId: String(group.id),
        });
      });
    });
    grouped.otherLabels.forEach(label => {
      list.push({
        ...label,
        groupName: t('SUMMARY_REPORTS.OTHER_LABELS'),
        groupId: 'other',
      });
    });
    return list;
  }

  if (activeGroupTab.value === 'other') {
    return grouped.otherLabels.map(label => ({
      ...label,
      groupName: t('SUMMARY_REPORTS.OTHER_LABELS'),
      groupId: 'other',
    }));
  }

  const targetGroup = grouped.groups.find(
    g => String(g.id) === String(activeGroupTab.value)
  );
  return (targetGroup?.labels || []).map(label => ({
    ...label,
    groupName: targetGroup.name,
    groupId: String(targetGroup.id),
  }));
});

const tableData = computed(() =>
  processedRowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      name:
        props.type === 'label'
          ? cleanLabelTitle(row)
          : row.name ?? row.title,
      color: row.color,
      groupName: row.groupName,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      resolutionsCount: renderCount(resolvedConversationsCount),
    };
  })
);

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  if (props.type === 'label') {
    store.dispatch('attributes/get');
  }
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector @filter-change="onFilterChange" />

  <div
    v-if="type === 'label' && availableGroups.length > 1"
    class="flex items-center gap-2 mt-4 overflow-x-auto pb-1"
  >
    <button
      v-for="tab in availableGroups"
      :key="tab.id"
      type="button"
      class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border flex items-center gap-1.5 whitespace-nowrap"
      :class="
        activeGroupTab === tab.id
          ? 'bg-n-brand text-white border-n-brand shadow-sm'
          : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:text-n-slate-12 hover:bg-n-solid-3'
      "
      @click="activeGroupTab = tab.id"
    >
      <span>{{ tab.name }}</span>
      <span
        class="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
        :class="
          activeGroupTab === tab.id
            ? 'bg-white/20 text-white'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{ tab.count }}
      </span>
    </button>
  </div>

  <div
    class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table :table="table" />
  </div>
</template>
